import { useState } from "react";

export const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="19" r="1" fill="currentColor" />
  </svg>
);

export const NoDataFound = () => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
    <span className="text-gray-400 text-sm font-medium">No Data Found</span>
  </div>
);

export const MetricCard = ({ title, value = "0 s" }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col relative">
    <div className="flex justify-between items-start mb-1">
      <span className="text-xs text-gray-500 font-medium leading-tight pr-4">{title}</span>
      <button className="text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0">
        <MoreIcon />
      </button>
    </div>
    <div className="flex items-center justify-center flex-1 py-4">
      <span className="text-5xl font-light text-gray-800 tracking-tight">{value}</span>
    </div>
  </div>
);

// A widget-card wrapper used for the two donut chart cards (attendee / status)
export const CardHeader = ({ title }) => (
  <div className="flex justify-between items-start mb-2">
    <span className="text-xs text-gray-500 font-medium">{title}</span>
    <button className="text-gray-400 hover:text-gray-600">
      <MoreIcon />
    </button>
  </div>
);

// Small legend swatch: solid for "below threshold", diagonal-striped (same hue) for "above threshold"
const Swatch = ({ variant = "below" }) => <span className="w-2.5 h-2.5 rounded-[2px] flex-shrink-0" style={variant === "above" ? { backgroundImage: "repeating-linear-gradient(45deg, #0b3a55 0 1.5px, #16597e 1.5px 3px)" } : { backgroundColor: "#7ecdf0" }} />;

// Below/above threshold split, rendered under a metric or donut card
export const SplitRow = ({ belowLabel, belowValue, aboveLabel, aboveValue }) => (
  <div className="border-t border-dashed border-gray-200 pt-2 mt-2 flex flex-col gap-1.5">
    <div className="flex items-center justify-between text-[11px]">
      <span className="flex items-center gap-1.5 text-gray-500 font-medium">
        <Swatch variant="below" />
        {belowLabel}
      </span>
      <span className="font-semibold text-gray-700">{belowValue}</span>
    </div>
    <div className="flex items-center justify-between text-[11px]">
      <span className="flex items-center gap-1.5 text-gray-500 font-medium">
        <Swatch variant="above" />
        {aboveLabel}
      </span>
      <span className="font-semibold text-gray-700">{aboveValue}</span>
    </div>
  </div>
);

// A metric card (headline number) with the below/above threshold split baked into the same card
export const MetricSplitCard = ({ title, value = "0 s", belowLabel, belowValue, aboveLabel, aboveValue }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col relative">
    <div className="flex justify-between items-start mb-1">
      <span className="text-xs text-gray-500 font-medium leading-tight pr-4">{title}</span>
      <button className="text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0">
        <MoreIcon />
      </button>
    </div>
    <div className="flex items-center py-2">
      <span className="text-4xl font-light text-gray-800 tracking-tight">{value}</span>
    </div>
    <SplitRow belowLabel={belowLabel} belowValue={belowValue} aboveLabel={aboveLabel} aboveValue={aboveValue} />
  </div>
);

// A donut card (attendee / status charts) with the below/above threshold split baked into the same card
export const DonutSplitCard = ({ title, children, belowLabel, belowValue, aboveLabel, aboveValue }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
    <CardHeader title={title} />
    {children}
    <SplitRow belowLabel={belowLabel} belowValue={belowValue} aboveLabel={aboveLabel} aboveValue={aboveValue} />
  </div>
);

// ---------------------------------------------------------------------------
// Redesigned "GHL Incoming Calls"-style pieces: hero KPI row, threshold
// breakdown cards (progress bar), and compact bar-comparison cards.
// ---------------------------------------------------------------------------

// One tile in the 4-up hero row. `subtext` is the small line under the number.
export const KpiTile = ({ title, value, subtext }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col relative">
    <div className="flex justify-between items-start mb-1">
      <span className="text-xs text-gray-500 font-medium leading-tight pr-4">{title}</span>
      <button className="text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0">
        <MoreIcon />
      </button>
    </div>
    <span className="text-3xl font-light text-gray-800 tracking-tight mt-2">{value}</span>
    {subtext ? <span className="text-[11px] text-gray-400 font-medium mt-1">{subtext}</span> : null}
  </div>
);

// Inline editable "threshold" pill, e.g. sits next to a section title.
// value/draft are controlled by the parent; onApply fires with the parsed number.
export const ThresholdPill = ({ value, draft, onDraftChange, onApply }) => {
  const [editing, setEditing] = useState(false);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      onApply(parsed);
    }
    setEditing(false);
  };

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1 hover:bg-sky-100 transition-colors" title="Click to change the duration threshold">
        Threshold: {value}s
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 bg-sky-50 border border-sky-200 rounded-full pl-2.5 pr-1 py-1">
      <input
        autoFocus
        type="number"
        min={1}
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-14 bg-transparent text-xs font-medium text-sky-700 focus:outline-none"
      />
      <span className="text-xs text-sky-700">s</span>
      <button onClick={commit} className="text-[11px] font-semibold text-white bg-sky-500 hover:bg-sky-600 rounded-full px-2 py-0.5 transition-colors">
        Apply
      </button>
    </div>
  );
};

// Two-color progress bar: light blue = below threshold, navy = above threshold
const ThresholdBar = ({ belowPct }) => (
  <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
    <div className="h-full" style={{ width: `${belowPct}%`, backgroundColor: "#7ecdf0" }} />
    <div className="h-full flex-1" style={{ backgroundColor: "#0b3a55" }} />
  </div>
);

// Duration threshold breakdown card: headline value + progress bar + below/above numbers with % of total
export const ThresholdSplitCard = ({ title, value, belowLabel, belowValue, aboveLabel, aboveValue, belowPct, abovePct }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col relative">
    <div className="flex justify-between items-start mb-1">
      <span className="text-xs text-gray-500 font-medium leading-tight pr-4">{title}</span>
      <button className="text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0">
        <MoreIcon />
      </button>
    </div>
    <span className="text-3xl font-light text-gray-800 tracking-tight mt-1 mb-3">{value}</span>
    <ThresholdBar belowPct={belowPct} />
    <div className="flex items-center justify-between mt-3">
      <div className="flex items-center gap-1.5">
        <Swatch variant="below" />
        <div className="leading-tight space-x-4">
          <span className="text-[11px] text-gray-500 font-medium">{belowLabel}</span>
          <span className="text-xs font-semibold text-gray-700">
            {belowValue} <span className="text-gray-400 font-normal">({belowPct}%)</span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="leading-tight space-x-4 text-right">
          <span className="text-[11px] text-gray-500 font-medium">{aboveLabel}</span>
          <span className="text-xs font-semibold text-gray-700">
            {aboveValue} <span className="text-gray-400 font-normal">({abovePct}%)</span>
          </span>
        </div>
        <Swatch variant="above" />
      </div>
    </div>
  </div>
);

// One horizontal bar within a CompareBarCard
const CompareBarRow = ({ label, belowValue, aboveValue, maxValue }) => {
  const belowWidth = maxValue > 0 ? Math.max((belowValue / maxValue) * 100, belowValue > 0 ? 4 : 0) : 0;
  const aboveWidth = maxValue > 0 ? Math.max((aboveValue / maxValue) * 100, aboveValue > 0 ? 4 : 0) : 0;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-gray-600 font-medium truncate">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className="h-2 rounded-full" style={{ width: `${belowWidth}%`, backgroundColor: "#7ecdf0", minWidth: belowValue > 0 ? "3px" : 0 }} />
        <span className="text-[10px] text-gray-500 font-semibold w-5">{belowValue}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="h-2 rounded-full" style={{ width: `${aboveWidth}%`, backgroundColor: "#0b3a55", minWidth: aboveValue > 0 ? "3px" : 0 }} />
        <span className="text-[10px] text-gray-500 font-semibold w-5">{aboveValue}</span>
      </div>
    </div>
  );
};

// Compact bar-comparison card: for each attendee/status, shows below vs above counts as bars
export const CompareBarCard = ({ title, belowLabel, aboveLabel, rows }) => {
  const maxValue = rows.reduce((max, r) => Math.max(max, r.belowValue, r.aboveValue), 0);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
      <CardHeader title={title} />
      <div className="flex items-center gap-3 mb-3 text-[11px]">
        <span className="flex items-center gap-1.5 text-gray-500 font-medium">
          <Swatch variant="below" />
          {belowLabel}
        </span>
        <span className="flex items-center gap-1.5 text-gray-500 font-medium">
          <Swatch variant="above" />
          {aboveLabel}
        </span>
      </div>
      {rows.length ? (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <CompareBarRow key={row.label} label={row.label} belowValue={row.belowValue} aboveValue={row.aboveValue} maxValue={maxValue} />
          ))}
        </div>
      ) : (
        <NoDataFound />
      )}
    </div>
  );
};
